<template>
  <div
    class="flicking-container d-flex flex-column align-center"
    :class="{
      'fade-css': fadeCss && !arrows,
      'fade-css--prev': fadeCssPrev,
      'fade-css--next': fadeCssNext
    }"
  >
    <flicking
      ref="carousel"
      :options="options"
      @ready="setUp"
      @after-resize="handleAfterResize"
      @move-end="handleMoveEnd"
      @select="handleSelect"
      @visible-change="updateFades"
    >
      <div
        v-show="arrows"
        slot="viewport"
        class="flicking-arrow-prev"
        data-cy="UlCarouselArrowPrevious"
        :class="[
          `flicking-arrow-prev--${arrowStyle}`,
          {
            'rounded-circle pa-3 border': arrowStyle === 'card',
            'align-center': arrowStyle === 'chip'
          }
        ]"
        @click="$emit('prevClick')"
      >
        <div class="icon" />
      </div>
      <slot />
      <div
        v-show="arrows"
        slot="viewport"
        class="flicking-arrow-next"
        data-cy="UlCarouselArrowNext"
        :class="[
          `flicking-arrow-next--${arrowStyle}`,
          {
            'rounded-circle pa-3 border': arrowStyle === 'card',
            'align-center': arrowStyle === 'chip'
          }
        ]"
        @click="$emit('nextClick')"
      >
        <div class="icon" />
      </div>
    </flicking>
    <div
      class="d-none flicking-pagination justify-center"
      :class="[{ 'd-md-flex': pagination }, paginationClasses]"
    />
  </div>
</template>

<script>
import { Arrow, Pagination, Fade } from '@egjs/flicking-plugins';
// TODO: make it work correctly, now it kinda flicks
// import { CustomFade } from '@/assets/js/flicking-fade-custom'

export default {
  name: 'UlCarousel',
  props: {
    fade: {
      type: Boolean,
      default: false
    },
    fadeCss: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Boolean,
      default: false
    },
    paginationClasses: {
      type: Array,
      required: false,
      default: () => ['pt-12']
    },
    arrows: {
      type: Boolean,
      default: true
    },
    arrowStyle: {
      type: String,
      default: 'card',
      validator(value) {
        return ['card', 'chip'].includes(value);
      }
    },
    align: {
      type: String,
      default: 'prev',
      validator(value) {
        return value === 'prev' || value === 'center';
      }
    },
    circular: {
      type: Boolean,
      default: false
    },
    interruptible: {
      type: Boolean,
      default: false
    },
    viewportClasses: {
      type: Array,
      required: false,
      default: () => []
    },
    viewportBottomPadding: {
      type: Number,
      required: false,
      default: 0
    }
  },
  emits: ['nextSwipe', 'prevSwipe', 'nextClick', 'prevClick'],
  data() {
    return {
      fadeCssPrev: false,
      fadeCssNext: false
    };
  },
  computed: {
    options() {
      return {
        align: this.align,
        autoResize: true,
        bound: true,
        circular: this.circular,
        interruptable: this.interruptible,
        threshold: 0,
        changeOnHold: false
      };
    }
  },
  watch: {
    options: {
      handler(newVal, oldVal) {
        if (newVal.align !== oldVal.align && this.$refs.carousel) {
          this.setUp();
        }
      },
      deep: true
    }
  },
  mounted() {
    window
      .matchMedia('(orientation: portrait)')
      .addEventListener('change', this.handleOrientationChange);
  },
  beforeDestroy() {
    window
      .matchMedia('(orientation: portrait)')
      .removeEventListener('change', this.handleOrientationChange);
  },
  methods: {
    handleOrientationChange() {
      if (!this.$refs.carousel) {
        return;
      }

      this.$refs.carousel.align =
        window.innerWidth >= this.$vuetify.breakpoint.thresholds.sm ? this.align : 'prev';

      this.setUp();
    },
    handleAfterResize(event) {
      if (event.sizeChanged) {
        this.setUp();
      }
    },
    async handleSelect(event) {
      this.$refs.carousel.disableInput();
      await this.$refs.carousel.moveTo(event.index > 0 ? event.index - 1 : 0);
      this.$refs.carousel.enableInput();
    },
    setUp() {
      const newPlugins = [new Fade('', this.$vuetify.breakpoint.mdAndUp && this.fade ? 1 : 0)];

      if (this.pagination) {
        newPlugins.push(
          new Pagination({
            parentEl: this.$el
          })
        );
      }

      if (this.arrows) {
        newPlugins.push(new Arrow());
      }

      this.$refs.carousel.removePlugins(...this.$refs.carousel.activePlugins);
      this.$refs.carousel.init();
      this.$refs.carousel.align = this.$vuetify.breakpoint.mdAndUp ? this.align : 'prev';
      this.$refs.carousel.addPlugins(...newPlugins);

      // fix various style issues which are impossible to fix by other means
      // add required CSS classes without the classes already added to the flicking-viewport element
      const classesToAddToViewport = this.viewportClasses?.filter(
        cls => !this.$refs.carousel.viewport.element.classList.contains(cls)
      );
      if (classesToAddToViewport && classesToAddToViewport.length) {
        this.$refs.carousel.viewport.element.classList.add(...classesToAddToViewport);
      }

      // compensate arrows for bottom padding, which is usually needed to not cut box-shadow
      if (this.viewportBottomPadding) {
        this.$refs.carousel.viewport.element.style.paddingBottom = `${this.viewportBottomPadding}px`;
        document.querySelectorAll('.flicking-arrow-next, .flicking-arrow-prev').forEach(el => {
          el.style.top = `calc(50% - ${this.viewportBottomPadding / 2}px)`;
        });
      }

      this.updateFades();
    },
    handleMoveEnd(event) {
      this.updateFades();
      if (event.isTrusted) {
        this.$emit(`${event.direction.toLowerCase()}Swipe`);
      }
    },
    updateFades() {
      if (!this.fadeCss) {
        return;
      }

      const visiblePanelsIndexes = this.$refs.carousel.visiblePanels.map(panel => panel.index);
      const flickingCamera = this.$refs.carousel.camera;

      this.fadeCssPrev = !visiblePanelsIndexes.includes(0) || flickingCamera.position > 0;
      this.fadeCssNext =
        !visiblePanelsIndexes.includes(this.$refs.carousel.panelCount - 1) ||
        flickingCamera.position < flickingCamera.range.max;
    }
  }
};
</script>
